import React, { useState } from 'react';
import { Autocomplete, Box, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { useDebounce } from 'use-debounce';

import { PLACES_MIN_INPUT_LENGTH, usePlaceSuggestions } from 'api/searchPlaces';
import googleMapsLogo from 'assets/google-maps-logo.svg';

export const defaultPlaceTexts = {
  placeholder: 'Search city or region',
  typeMore: `Type at least ${PLACES_MIN_INPUT_LENGTH} characters`,
  noResults: 'No places found',
  unavailable: 'Location search is temporarily unavailable',
  rateLimited: 'Too many searches, please try again in a minute',
  failed: 'Location search failed',
  poweredBy: 'Google Maps',
};

const AttributionPaper = ({ attribution, children, ...props }) => (
  <Paper {...props}>
    {children}
    {attribution && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
        <Box component="img" src={googleMapsLogo} alt={attribution} sx={{ height: 14, width: 'auto' }} />
      </Box>
    )}
  </Paper>
);

const getErrorText = (error, texts) => {
  switch (error?.response?.data?.code) {
    case 'places_quota_exceeded':
      return texts.unavailable;
    case 'places_rate_limited':
      return texts.rateLimited;
    default:
      return texts.failed;
  }
};

export const PlaceAutocomplete = ({
  value,
  onChange,
  multiple = false,
  isPublic = false,
  lang = 'en',
  disabled = false,
  error = null,
  size = 'medium',
  texts: customTexts = {},
}) => {
  const texts = { ...defaultPlaceTexts, ...customTexts };
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const { data: suggestions = [], isFetching, error: searchError } = usePlaceSuggestions({
    input: debouncedSearchTerm,
    lang,
    isPublic,
  });

  const options = suggestions.map((suggestion) => ({
    place_id: suggestion.place_id,
    name: suggestion.main_text || suggestion.text,
    secondaryText: suggestion.secondary_text,
  }));

  const getNoOptionsText = () => {
    if (searchTerm.trim().length < PLACES_MIN_INPUT_LENGTH) return texts.typeMore;
    if (searchError) return getErrorText(searchError, texts);
    return texts.noResults;
  };

  return (
    <Autocomplete
      multiple={multiple}
      value={value}
      onChange={(_, newValue) => {
        onChange(newValue);
        setSearchTerm('');
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') setSearchTerm(newInputValue);
        if (reason === 'clear') setSearchTerm('');
      }}
      options={options}
      filterOptions={(x) => x}
      filterSelectedOptions={multiple}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, selected) => option.place_id === selected.place_id}
      loading={isFetching}
      noOptionsText={getNoOptionsText()}
      disabled={disabled}
      size={size}
      fullWidth
      sx={{
        '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
        },
        '& .MuiAutocomplete-tag': { my: 0.5, ml: 1, mr: 0, fontSize: '0.875rem' },
      }}
      slots={{ paper: AttributionPaper }}
      slotProps={{ paper: { attribution: options.length ? texts.poweredBy : null } }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps}>
            <Box>
              <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
              {option.secondaryText && (
                <Typography variant="caption" color="text.secondary">{option.secondaryText}</Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={texts.placeholder}
          error={!!error}
          helperText={error?.message}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
